import type { ChannelsPage } from '../../api/signalOps';
import { formatNumber } from '../../utils/numberFormat';
import { formatEuroCompact } from './format';

export type DemoBridge = NonNullable<ChannelsPage['demos']>;

/** "+5", "−3": a signed count for the bridge, with a true minus sign. */
export function signed(n: number): string {
  return `${n < 0 ? '−' : '+'}${formatNumber(Math.abs(n))}`;
}

/** Signed booking credit, one decimal like the Booked column. */
export function signedCredit(n: number): string {
  return `${n < 0 ? '−' : '+'}${Math.abs(n).toFixed(1)}`;
}

/** The edge term's label: negative is only the period edge; positive also holds rebookings. */
export function edgeLabel(other: number): string {
  return other < 0 ? 'Dated across the period edge' : 'Rebooked within 30 days, or across the period edge';
}

/**
 * "57 demos on Sales › Demo bookings = 34 matched to a company + 21 no company
 * match + 5 cancelled − 3 dated across the period edge": the bridge in words,
 * every term signed once, so it reads as a sum.
 */
export function demoBridgeText(d: DemoBridge): string {
  const terms: string[] = [`${formatNumber(d.matched)} matched to a company`];
  if (d.noCompany) terms.push(`+ ${formatNumber(d.noCompany)} with no company match`);
  if (d.cancelled) terms.push(`+ ${formatNumber(d.cancelled)} cancelled (Sales counts them, attribution reads live bookings)`);
  if (d.other) terms.push(`${d.other < 0 ? '−' : '+'} ${formatNumber(Math.abs(d.other))} ${edgeLabel(d.other).toLowerCase()}`);
  return `${formatNumber(d.sales)} demos on Sales › Demo bookings = ${terms.join(' ')}.`;
}

/** The tile's short form of the same bridge: "34 matched · 21 no company match · 5 cancelled · −3 period edge". */
export function demoBridgeShort(d: DemoBridge): string {
  return [
    `${formatNumber(d.matched)} matched`,
    d.noCompany ? `${formatNumber(d.noCompany)} no company match` : null,
    d.cancelled ? `${formatNumber(d.cancelled)} cancelled` : null,
    d.other ? `${signed(d.other)} period edge` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Above this share of Generated sitting on "No touches", the page flags it. */
export const NO_TOUCH_FLAG_SHARE = 0.5;

/**
 * "€585.7k of €765.7k (76%) generated sits on No touches": the exception the
 * channel rows can't show, said when it dominates. Null below the flag share
 * or with nothing generated.
 */
export function noTouchException(unattributedGenerated: number, totalGenerated: number): { share: number; text: string } | null {
  if (!(totalGenerated > 0) || !(unattributedGenerated > 0)) return null;
  const share = unattributedGenerated / totalGenerated;
  if (share < NO_TOUCH_FLAG_SHARE) return null;
  return {
    share,
    text: `${formatEuroCompact(unattributedGenerated)} of ${formatEuroCompact(totalGenerated)} generated (${Math.round(
      share * 100,
    )}%) sits on No touches: the account booked before any channel signal reached it, so no channel gets that credit.`,
  };
}

/**
 * The Generated tile's deal-value line and the footer's Confidence note, from
 * one set of numbers: "3 of 12 matched bookings at real deal value" and
 * "no booking a channel touched is" can both be true when those 3 had no
 * channel touch, and the copy now says so instead of reading as a contradiction.
 */
export function dealValueNote(withDealValue: number, bookings: number, channelDealValued: number): string {
  const head = `${formatNumber(withDealValue)} of ${formatNumber(bookings)} matched bookings at real deal value`;
  if (withDealValue > 0 && channelDealValued === 0) return `${head}, all on No touches`;
  return head;
}
